import { useField } from 'formik';
import {
  FormGroup,
  Label,
  Input,
  InputGroupText,
  FormFeedback,
  FormText,
  Row,
  Col,
} from 'reactstrap';
import { InputProps } from 'reactstrap';
import { useTranslator } from '../I18n/useTranslator';
import React, { ChangeEvent } from 'react';

interface IFormikInputProps extends InputProps {
  label?: React.ReactNode;
  required?: boolean;
  callbackOnChange?: (value: string | number) => void;
  translateErrMsg?: boolean;
  text?: string;
  unit?: string;
  name: string;
  disableMetaTouched?: boolean;
  horizontal?: boolean;
  className?: string;
}

const FormikInput: React.FC<IFormikInputProps> = ({
  label,
  required = false,
  callbackOnChange,
  translateErrMsg = false,
  text = '',
  disableMetaTouched = false,
  horizontal = false,
  ...props
}) => {
  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input>. We can use field meta to show an error
  // message if the field is invalid and it has been touched (i.e. visited)
  const [field, meta, helpers] = useField(props.name);
  const { t } = useTranslator();

  if (props.type === 'hidden') {
    return <Input {...field} {...props} />;
  }

  const classNameArray = ['mb-m'];
  if (props.disabled) {
    classNameArray.push('disabled');
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (props.type === 'number') {
      if (props.step) {
        if (value === '') {
          helpers.setValue('');
        } else {
          const number = +value;
          switch (props.step) {
            case '1':
              helpers.setValue(Math.round(number));
              break;
            case '0.1':
              helpers.setValue(Math.round(number * 10) / 10);
              break;
            case '0.01':
              helpers.setValue(Math.round(number * 100) / 100);
              break;
            case '0.001':
              helpers.setValue(Math.round(number * 1000) / 1000);
              break;
            default:
              helpers.setValue(number);
          }
        }
      } else {
        helpers.setValue(value === '' ? '' : +value);
      }
    } else {
      helpers.setValue(value);
    }

    if (callbackOnChange) {
      callbackOnChange(value);
    }
  };

  if (disableMetaTouched) {
    meta.touched = true;
  }

  return (
    <FormGroup className={`${classNameArray.join(' ')} ${props.className}`}>
      {horizontal ? (
        <Row className="align-items-center">
          {label && (
            <Col xs={6}>
              <p className="mb-0 text-start">
                {required && <sup>*</sup>} {label}
              </p>
            </Col>
          )}
          <Col
            xs={label ? 6 : 12}
            className={meta.touched && meta.error ? 'is-invalid' : undefined}
          >
            <div
              className={`${props.unit ? 'input-group-with-addon' : ''} ${
                meta.touched && meta.error ? 'is-invalid' : ''
              }`}
            >
              <Input
                {...field}
                {...props}
                invalid={!!(meta.touched && meta.error)}
                onChange={handleChange}
                data-test-id={`${props.name}_input`}
                inputMode={props.type === 'number' ? 'decimal' : 'text'} // for iOS numeric keyboard
              />
              {props.unit ? <InputGroupText>{props.unit}</InputGroupText> : null}
            </div>
          </Col>
          {meta.touched && meta.error && (
            <FormFeedback className="col-12 text-center">
              {translateErrMsg ? t(meta.error) : meta.error}
            </FormFeedback>
          )}
          {text && !(meta.touched && meta.error) && !meta.value && (
            <FormText className="w-100 col-12">{text}</FormText>
          )}
        </Row>
      ) : (
        <>
          {label && (
            <Label for={props.id || props.name}>
              {required && <sup>*</sup>} {label}
            </Label>
          )}
          <div
            className={`${props.unit ? 'input-group-with-addon' : ''} ${
              meta.touched && meta.error ? 'is-invalid' : ''
            }`}
          >
            <Input
              {...field}
              {...props}
              invalid={!!(meta.touched && meta.error)}
              onChange={handleChange}
              data-test-id={`${props.name}_input`}
              inputMode={props.type === 'number' ? 'decimal' : 'text'} // for iOS numeric keyboard
            />
            {props.unit ? <InputGroupText>{props.unit}</InputGroupText> : null}
          </div>
          {meta.touched && meta.error && (
            <FormFeedback>{translateErrMsg ? t(meta.error) : meta.error}</FormFeedback>
          )}
          {text && !(meta.touched && meta.error) && !meta.value && (
            <FormText className="w-100">{text}</FormText>
          )}
        </>
      )}
    </FormGroup>
  );
};

export default FormikInput;
